import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/StatusBadge";
import NewTicketSheet from "@/components/NewTicketSheet";
import TicketDetailSheet from "@/components/TicketDetailSheet";
import { Plus, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function MyTickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tickets", { params: { scope: "mine" } });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-8" data-testid="my-tickets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-zinc-500">Your requests</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-950 mt-1">My Tickets</h1>
          <p className="text-sm text-zinc-500 mt-1">Track every request you've submitted.</p>
        </div>
        <Button
          onClick={() => setOpenNew(true)}
          data-testid="new-ticket-button"
          className="bg-[#0055FF] hover:bg-[#0040CC] text-white h-11 rounded-md transition-colors duration-200"
        >
          <Plus className="h-4 w-4 mr-2" /> Submit new ticket
        </Button>
      </div>

      {tickets.length === 0 ? (
        <div className="mt-10 bg-white border border-zinc-200 rounded-md p-16 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-zinc-100 grid place-items-center">
            <Inbox className="h-5 w-5 text-zinc-500" />
          </div>
          <h3 className="mt-4 font-heading text-lg font-semibold text-zinc-950">No tickets yet</h3>
          <p className="mt-1 text-sm text-zinc-500">Submit your first request to get help from IT or Maintenance teams.</p>
          <Button
            onClick={() => setOpenNew(true)}
            className="mt-6 bg-[#0055FF] hover:bg-[#0040CC] text-white"
            data-testid="empty-new-ticket-button"
          >
            <Plus className="h-4 w-4 mr-2" /> Submit ticket
          </Button>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setDetailId(t.id)}
              data-testid={`my-ticket-card-${t.id}`}
              className="text-left bg-white border border-zinc-200 rounded-md p-5 hover:border-zinc-400 hover:-translate-y-[1px] transition-transform duration-200"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-mono text-zinc-500">#{t.id.slice(0,8)}</div>
                  <h3 className="font-heading text-lg font-semibold text-zinc-950 mt-0.5 truncate">{t.title}</h3>
                </div>
                <Badge kind="status" value={t.status} />
              </div>
              <p className="text-sm text-zinc-600 mt-2 line-clamp-2">{t.description}</p>
              <div className="mt-4 flex items-center flex-wrap gap-2">
                <Badge kind="dept" value={t.department} />
                <Badge kind="priority" value={t.priority} />
                <span className="text-xs text-zinc-500 ml-auto">{new Date(t.created_at).toLocaleDateString()}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <NewTicketSheet open={openNew} onOpenChange={setOpenNew} onCreated={load} />
      <TicketDetailSheet ticketId={detailId} open={!!detailId} onOpenChange={(v) => !v && setDetailId(null)} onChanged={load} />
    </div>
  );
}
